import { useMemo } from 'react';
import type React from 'react';

import { Link } from '@change/ds-core-ui/Link';
import { Separator } from '@change/ds-core-ui/Separator';
import { Text } from '@change/ds-core-ui/Text';

import { EmbeddedMedia } from '#src/app/shared/components/embeddedMedia';
import { Html } from '#src/app/shared/components/html';
import { useRichMediaTransformers } from '#src/app/shared/hooks/richMedia';

import { Actions } from './components/Actions';
import { ShareLinks } from './components/ShareLinks';
import { usePageBody } from './hooks/usePageBody';

export const PageBody: React.FC<EmptyProps> = () => {
	const {
		data: { description, isSimpleUrlDescription, creationDate, embeddedMedia, link },
		actions: { handleLinkClick },
	} = usePageBody();

	// The description stores inline images/video from the editor as encoded `change-media` tags.
	// The decode transformer turns them back into <img>/<iframe> and whitelists the tag past sanitization.
	const { decodeRichMediaTransformer } = useRichMediaTransformers();
	const htmlTransformers = useMemo(() => [decodeRichMediaTransformer], [decodeRichMediaTransformer]);

	return (
		<div className="flex flex-col gap-6 md:gap-12">
			{isSimpleUrlDescription ? (
				// older version of the update form allowed to simply input a url that would end up being the description
				<div className="wrap-break-word">
					<Text className="text-typography-weak">{creationDate} —&nbsp;</Text>
					<Link href={description} data-qa="simple-description-link">
						{description}
					</Link>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<Text>{creationDate}</Text>
					<Html
						html={description}
						className="[&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full"
						transformers={htmlTransformers}
						data-qa="petition-update-description"
						expressive
					/>
				</div>
			)}
			{link && (
				<Link href={link.url} target="_blank" onClick={handleLinkClick} data-qa="news-article-link">
					{link.label}
				</Link>
			)}
			{embeddedMedia?.html && (
				<EmbeddedMedia
					html={embeddedMedia.html}
					aspectRatio={embeddedMedia.aspectRatio || undefined}
					className="mt-4"
					data-qa="embedded-media"
				/>
			)}
			<Actions />
			<div className="flex flex-col gap-6">
				<Separator className="my-0" />
				<ShareLinks />
			</div>
		</div>
	);
};
